import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi } from "@/lib/api";
import { useStream } from "@/lib/stream";
import { copyText } from "@/lib/copy";
import { Markdown } from "@/components/ai/Markdown";
import { WeeklyReport } from "@/components/ai/WeeklyReport";

const Pick = ({ value, onChange, items, all, testId }) => (
  <Select value={value || "all"} onValueChange={(v) => onChange(v === "all" ? "" : v)}>
    <SelectTrigger className="w-56 bg-white" data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent><SelectItem value="all">{all}</SelectItem>{items.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
  </Select>
);

export const EpiAnalysis = () => {
  const api = useApi();
  const stream = useStream();
  const [meta, setMeta] = useState({ pathologies: [], wilayas: [] });
  const [f, setF] = useState({ pathology: "", wilaya: "", days: "30" });
  const [out, setOut] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api("get", "/epidemic/meta").then(setMeta).catch(() => {}); }, [api]);
  const run = async () => {
    setBusy(true);
    setOut("");
    try { await stream("/ai/epidemic-analysis", { pathology: f.pathology || null, wilaya: f.wilaya || null, days: Number(f.days) }, setOut); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5" data-testid="ai-epi-panel">
      <WeeklyReport />
      <div className="flex flex-wrap items-center gap-3">
        <Pick value={f.pathology} onChange={(v) => setF({ ...f, pathology: v })} items={meta.pathologies.map((p) => [p, p])} all="Toutes les pathologies" testId="ai-epi-pathology" />
        <Pick value={f.wilaya} onChange={(v) => setF({ ...f, wilaya: v })} items={meta.wilayas.map((w) => [w.code, `${w.code} — ${w.name}`])} all="Toutes les wilayas" testId="ai-epi-wilaya" />
        <Select value={f.days} onValueChange={(v) => setF({ ...f, days: v })}>
          <SelectTrigger className="w-40 bg-white" data-testid="ai-epi-days"><SelectValue /></SelectTrigger>
          <SelectContent>{[["7", "7 jours"], ["30", "30 jours"], ["90", "90 jours"], ["365", "12 mois"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Button onClick={run} disabled={busy} className="bg-[#C2410C] hover:bg-[#9A3412] text-white" data-testid="ai-epi-run"><Wand2 className="h-4 w-4 mr-1.5" /> {busy ? "Analyse en cours…" : "Générer l'analyse"}</Button>
        {out && !busy && <Button variant="outline" onClick={() => copyText(out, "Analyse copiée")} data-testid="ai-epi-copy"><Copy className="h-4 w-4 mr-1.5" /> Copier</Button>}
      </div>
      <div className="bg-white border border-stone-200 rounded-lg p-6 min-h-[200px]">
        {out ? <Markdown testId="ai-epi-output">{out}</Markdown> : <p className="text-sm text-stone-500">{busy ? "Claude analyse les statistiques…" : "Choisissez un périmètre puis générez une analyse écrite des tendances et alertes."}</p>}
      </div>
    </div>
  );
};
